import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useLocalStorage } from "@/hooks/useLocalStorage"
import { useSessionStorage } from "@/hooks/useSessionStorage"
import { initData, retrieveLaunchParams } from "@telegram-apps/sdk-react"
import posthog from "posthog-js"
import * as Sentry from "@sentry/react"

import { getCatalogAuth } from "@utils/api/getCatalogAuth"
import { getTokens } from "@utils/api/getTokens"
import { getTokenPrices } from "@utils/api/getTokenPrices"
import { getUser } from "@utils/api/getUser"
import { getAuth } from "@utils/api/getAuth"
import { getTokenBalances } from "@utils/api/getTokenBalances"
import { getEmojis } from "@utils/api/getEmojis"
import { getBonus } from "@utils/api/getBonus"
import { getTasks } from "@utils/api/getTasks"
import { getUserTasks } from "@utils/api/getUserTasks"
import { isUnauthorizedError } from "@utils/isUnathorizedError"

import { balanceToken, fromDecimal, pingPeriod, starsToken } from "@uno/common"

import { TokenPrice } from "@typings/tokenPrice"
import { Token } from "@typings/token"
import { TokenBalance } from "@typings/tokenBalance"
import { User } from "@typings/user"
import { UserCore } from "@typings/userCore"
import { StreakData } from "@/typings/streakData"
import { Task } from "@typings/task"
import { getUserCore } from "@utils/api/getUserCore"
import { sendPing } from "@/utils/api/sendPing"

import { useTranslation } from "react-i18next"

interface UserContextType {
  allTokens: Token[]
  allTokensPrices: TokenPrice[]
  authCoreToken: string
  authToken: string
  balance: number
  currentToken: Token
  currentTokenBalance: string
  fetchTokenBalances: () => Promise<void>
  getAndSetAuthToken: () => Promise<string>
  fetchAndSetUser: () => Promise<User | null>
  fetchTasks: () => Promise<void>
  currentTokenId: number
  setCurrentTokenId: (value: number) => void
  streakData: StreakData
  streakLoaded: boolean
  tokenBalances: TokenBalance[]
  tasks: Task[]
  userTasks: Task[]
  updateBalance: (balance: number) => void
  updateStreakDays: (streakData: StreakData) => void
  updateTokenBalances: (tokenId: number, balance: string) => void
  user: User
  userCore: UserCore
  langLoading: boolean
}

const UserContext = createContext<UserContextType | null>(null)

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const launchParams = retrieveLaunchParams()
  const userData = initData.user()
  const userId = userData?.id
  const initDataRaw = initData.raw()

  const { i18n } = useTranslation()

  const [langLoading, setLangLoading] = useSessionStorage(
    "language_setting",
    false
  )

  const [authToken, setAuthToken] = useSessionStorage<string>(
    `${userId}_appToken`,
    ""
  )
  const [authCoreToken, setAuthCoreToken] = useSessionStorage<string>(
    `${userId}_token`,
    ""
  )

  const [user, setUser] = useLocalStorage<User | null>(
    `${userId}_userInfo`,
    null
  )
  const [balance, setBalance] = useLocalStorage<number>(`${userId}_balance`, 0)
  const [tokenBalances, setTokenBalances] = useLocalStorage<TokenBalance[]>(
    `${userId}_tokenBalances`,
    []
  )
  const [userCore, setUserCore] = useLocalStorage<UserCore | null>(
    `${userId}_userCoreInfo`,
    null
  )
  const [allTokens, setAllTokens] = useLocalStorage<Token[]>(
    `${userId}_allTokens`,
    []
  )
  const [allTokensPrices, setAllTokensPrices] = useLocalStorage<TokenPrice[]>(
    `${userId}_allTokensPrices`,
    []
  )
  const [currentTokenId, setCurrentTokenId] = useLocalStorage<number>(
    `${userId}_currentTokenId`,
    Number(import.meta.env.VITE_CORE_API_TOKEN_ID)
  )
  const [streakData, setStreakData] = useLocalStorage<StreakData | null>(
    `${userId}_streakData`,
    null
  )
  const [tasks, setTasks] = useLocalStorage<Task[]>(`${userId}_tasks`, [])
  const [userTasks, setUserTasks] = useLocalStorage<Task[]>(
    `${userId}_userTasks`,
    []
  )
  const [streakLoaded, setStreakLoaded] = useState(false)

  const APPLICATION_ID = import.meta.env.VITE_CORE_API_APP_ID
  const username = userData?.username
  const platform = launchParams.platform
  const version = launchParams.version
  const startParam = launchParams.startParam

  const getAndSetAuthToken = useCallback(async () => {
    if (authToken) return authToken

    if (!initDataRaw) return

    try {
      const token = await getAuth(initDataRaw)
      setAuthToken(token)

      try {
        const data = await getBonus(token)
        setStreakData(data)
      } catch (error) {
        if (error instanceof Error) {
          if (error.name === "TimeoutError") {
            console.error("Bonus data request timed out")
          } else {
            console.error("Error fetching bonus data:", error.message)
          }
          setStreakData(null)
          setStreakLoaded(true)
        } else {
          console.error("Unexpected error fetching bonus data:", error)
        }
      }

      void getEmojis(token)
      return token
    } catch (error) {
      console.error("Error obtaining auth token:", error)
    }
  }, [authToken, initDataRaw, setAuthToken])

  const getAndSetAuthCoreToken = useCallback(
    async (forceRefresh = false) => {
      if (authCoreToken && !forceRefresh) return authCoreToken

      if (!initDataRaw || !userId) return

      try {
        const token = await getCatalogAuth(initDataRaw, userId)
        setAuthCoreToken(token)

        return token
      } catch (error) {
        console.error("Error obtaining auth core token:", error)
      }
    },
    [authCoreToken, initDataRaw, userId]
  )

  const fetchAndSetUser = useCallback(async (): Promise<User | null> => {
    if (!initDataRaw) {
      console.error("No init data available for fetching user")
      return null
    }

    try {
      const tokenToUse = authToken || (await getAndSetAuthToken())
      if (!tokenToUse) {
        console.error("Could not obtain auth token for user fetch")
        return null
      }

      try {
        const userData = await getUser(tokenToUse)
        setUser(userData)
        setBalance(userData.balance)
        return userData
      } catch (error: unknown) {
        // If unauthorized, try to refresh token and fetch again
        if (isUnauthorizedError(error)) {
          const newToken = await getAndSetAuthToken()
          if (newToken) {
            try {
              const userData = await getUser(newToken)
              setUser(userData)
              setBalance(userData.balance)
              return userData
            } catch (secondError) {
              console.error(
                "Failed to fetch user with refreshed token:",
                secondError
              )
            }
          }
        } else {
          console.error("Error fetching user data:", error)
        }
      }
    } catch (error) {
      console.error("Unhandled error in fetchAndSetUser:", error)
    }

    return null
  }, [initDataRaw, authToken, getAndSetAuthToken, setUser, setBalance])

  const fetchUserCore = useCallback(async () => {
    if (!authCoreToken) return

    try {
      const userCore = await getUserCore(authCoreToken)

      setUserCore(userCore)
      posthog.identify(
        `${userCore.id}-${APPLICATION_ID}`,
        {
          username,
          latestTgMiniAppPlatform: platform,
          latestTgMiniAppVersion: version,
          latestStartParam: startParam,
        },
        {
          startParam,
          applicationId: APPLICATION_ID,
          telegramId: userId,
          userId: userCore.id,
        }
      )

      Sentry.setUser({
        applicationId: APPLICATION_ID,
        telegramId: userId,
        userId: userCore.id,
      })
    } catch (error) {
      if (isUnauthorizedError(error)) {
        const newToken = await getAndSetAuthCoreToken(true)
        if (newToken) await fetchUserCore()
      } else console.error("Error fetching user core:", error)
    }
  }, [
    authCoreToken,
    userId,
    APPLICATION_ID,
    username,
    platform,
    version,
    setUserCore,
    getAndSetAuthCoreToken,
  ])

  const fetchTokenBalances = useCallback(async () => {
    if (!authCoreToken) return

    try {
      const tokens = await getTokenBalances(authCoreToken)
      setTokenBalances(tokens)
    } catch (error) {
      console.error("Error fetching token balances:", error)
    }
  }, [authCoreToken, setTokenBalances])

  const fetchAllTokens = useCallback(async () => {
    if (!authCoreToken) return

    try {
      const tokens = await getTokens(authCoreToken)
      setAllTokens(tokens)
    } catch (error) {
      console.error("Error fetching all tokens:", error)
    }
  }, [authCoreToken, setAllTokens])

  const fetchAllTokenPrices = useCallback(async () => {
    if (!authCoreToken) return

    try {
      const prices = await getTokenPrices(authCoreToken)
      if (prices) setAllTokensPrices(prices)
    } catch (error) {
      console.error("Error fetching token prices:", error)
    }
  }, [authCoreToken, setAllTokensPrices])

  useEffect(() => {
    if (!authToken && initDataRaw) void getAndSetAuthToken()
    if (!authCoreToken && initDataRaw) void getAndSetAuthCoreToken()
  }, [
    authToken,
    authCoreToken,
    initDataRaw,
    getAndSetAuthToken,
    getAndSetAuthCoreToken,
  ])

  useEffect(() => {
    if (authToken) void fetchAndSetUser()
  }, [authToken, fetchAndSetUser])

  useEffect(() => {
    if (user && !langLoading) {
      if (user.lang) {
        i18n.changeLanguage(user.lang)
      }
      setLangLoading(true)
    }
  }, [user])

  const fetchTasks = useCallback(async () => {
    if (!authCoreToken) return

    try {
      const fetchedTasks = await getTasks(authCoreToken)
      setTasks(fetchedTasks)

      const fetchedUserTasks = await getUserTasks(authCoreToken)
      setUserTasks(fetchedUserTasks)
    } catch (error) {
      console.error("Error fetching tasks:", error)
    }
  }, [authCoreToken])

  useEffect(() => {
    if (authCoreToken) {
      void fetchUserCore()
      void fetchTokenBalances()
      void fetchAllTokens()
      void fetchAllTokenPrices()
      void fetchTasks()
    }
  }, [
    authCoreToken,
    fetchUserCore,
    fetchTokenBalances,
    fetchAllTokens,
    fetchAllTokenPrices,
    fetchTasks,
  ])

  useEffect(() => {
    if (authCoreToken) {
      void sendPing(authCoreToken)
    }

    const pingInterval = setInterval(() => {
      if (authCoreToken) {
        void sendPing(authCoreToken)
      } else if (initDataRaw) {
        void getAndSetAuthCoreToken()
      }
    }, pingPeriod)

    return () => clearInterval(pingInterval)
  }, [authCoreToken, initDataRaw, getAndSetAuthCoreToken])

  const currentToken = useMemo(() => {
    if (!tokenBalances || !allTokens) return null

    const effectiveTokenId =
      currentTokenId === balanceToken ? starsToken : currentTokenId

    const token =
      tokenBalances.find((b) => b.tokenId === effectiveTokenId) ||
      tokenBalances.find((b) => b.tokenId === starsToken)

    return token
      ? token.token
      : allTokens.find((t) => t.id === effectiveTokenId) ||
          allTokens.find((t) => t.id === starsToken)
  }, [tokenBalances, allTokens, currentTokenId])

  const currentTokenBalance = useMemo(() => {
    if (!tokenBalances || !currentToken) return "0"
    const tokenBalance = tokenBalances.find(
      (b) => b.tokenId === currentToken.id
    )
    if (!tokenBalance) return "0"
    return currentToken.decimals === 0
      ? Number(tokenBalance.balance).toFixed(2).replace(/\.00/, "")
      : Number(fromDecimal(tokenBalance.balance, currentToken.decimals))
          .toFixed(2)
          .replace(/\.00/, "")
  }, [tokenBalances, currentToken])

  const updateBalance = useCallback(
    (newBalance: number) => {
      setBalance(newBalance)
    },
    [setBalance]
  )

  const updateTokenBalances = useCallback(
    (tokenId: number, newBalance: string) => {
      const updatedBalances = tokenBalances.map((balance) =>
        balance.tokenId === tokenId
          ? { ...balance, balance: newBalance }
          : balance
      )
      setTokenBalances(updatedBalances)
    },
    [tokenBalances, setTokenBalances]
  )

  const updateStreakDays = useCallback(
    (newStreakData: StreakData) => {
      setStreakData(newStreakData)
    },
    [setStreakData]
  )

  const contextValue = useMemo(
    () => ({
      streakData,
      streakLoaded,
      user,
      userCore,
      balance,
      tokenBalances,
      authToken,
      authCoreToken,
      currentToken,
      currentTokenBalance,
      allTokens,
      allTokensPrices,
      currentTokenId,
      getAndSetAuthToken,
      setCurrentTokenId,
      updateBalance,
      updateTokenBalances,
      fetchTokenBalances,
      fetchAndSetUser,
      updateStreakDays,
      tasks,
      userTasks,
      fetchTasks,
      langLoading,
    }),
    [
      streakLoaded,
      streakData,
      user,
      userCore,
      balance,
      tokenBalances,
      authToken,
      authCoreToken,
      currentToken,
      currentTokenBalance,
      allTokens,
      allTokensPrices,
      currentTokenId,
      setCurrentTokenId,
      getAndSetAuthToken,
      updateBalance,
      updateTokenBalances,
      fetchTokenBalances,
      fetchAndSetUser,
      updateStreakDays,
      tasks,
      userTasks,
      fetchTasks,
      langLoading,
    ]
  )

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  )
}

export const useUser = () => {
  const context = useContext(UserContext)
  if (!context) throw new Error("useUser must be used within a UserProvider")

  return context
}
